import { CaseStatus } from '@bybeam/platform-types';
import { Close } from '@mui/icons-material';
import Popper from '@platform-ui-common/components/Popper/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import useProgress from '@platform-ui-common/hooks/useProgress/useProgress.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { formatPercentage } from '@platform-ui-common/utilities/format.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { Button, Dialog, Flex, IconButton, Link } from '@radix-ui/themes';
import { usePostHog } from 'posthog-js/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import Markdown from '../Markdown/index.js';
import ButtonComponent from './ButtonComponent.js';

export interface ProgressPopperProps {
  onClickClose: () => void;
  isOpen: boolean;
  anchorEl?: HTMLButtonElement;
}
export interface ApplicationProgressProps {
  onNavigate?: () => void;
}

export function ApplicationProgress({ onNavigate }: ApplicationProgressProps): JSX.Element {
  const {
    options: { label, disableSkip, getRoute },
    progress: { percentage, steps },
    currentStep,
  } = useProgress();

  const progressBarLabelId = uuid();
  const navLabelId = uuid();

  const posthog = usePostHog();

  const config = useApplicationConfig();
  const introPages = config?.introPages;
  const placeholders = usePlaceholder();

  const application = useApplication();

  return (
    <div className="flex flex-col py-14 lg:py-4 lg:text-left">
      <div className="mb-2">
        <Typography variant="body" color={Color.Text}>
          Application Progress
        </Typography>
      </div>
      <div className="flex flex-col mb-6 w=full">
        <small id={progressBarLabelId} className="mb-1" aria-hidden>
          <strong>{formatPercentage(percentage)} complete</strong>
        </small>
        <div
          role="progressbar"
          aria-labelledby={progressBarLabelId}
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={1}
          className="h-1 flex overflow-hidden relative bg-grayBackground w-full"
        >
          <div
            aria-hidden
            className="h-full bg-primary absolute"
            style={{ width: `${formatPercentage(percentage)}` }}
          />
        </div>
        {posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.resubmissionRequests) &&
          application?.case && application?.case.status !== CaseStatus.InProgress ? (
          <Flex pt="4">
            <Button asChild variant="outline">
              <ReactRouterLink
                to={makeRoute(ApplicantRoutes.CASE_DETAILS, { caseId: application.case.id })}
              >
                View case details
              </ReactRouterLink>
            </Button>
          </Flex>
        ) : null}
      </div>
      <div className="mb-6">
        <div id={navLabelId} className="mb-4" aria-hidden>
          <Typography variant="body">Application Sections</Typography>
        </div>
        <nav aria-labelledby={navLabelId}>
          <ul className="space-y-8 lg:space-y-5">
            {steps?.map(({ key, displayName, isComplete, route }, index) => (
              <li className="gap-y-2" key={key}>
                <ButtonComponent
                  onClickClose={onNavigate}
                  buttonText={`${displayName}`}
                  route={getRoute(route)}
                  icon={isComplete ? 'checkCircleOutline' : 'radioButtonUnchecked'}
                  disabled={disableSkip ? currentStep < index : false}
                />
              </li>
            ))}
          </ul>
        </nav>
      </div>
      {posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.introPagesNavigation) &&
        introPages?.length ? (
        <div>
          <div className="mb-2">
            <Typography variant="body" color={Color.Text}>
              Program Overview
            </Typography>
          </div>
          <Flex direction="column" gap="1" maxWidth="16rem" asChild>
            <ul>
              {introPages
                .filter((introPage) => introPage.layout !== 'steps')
                .map((introPage) => {
                  return (
                    <li key={introPage.key}>
                      <Dialog.Root>
                        <Dialog.Trigger className="cursor-pointer hover:underline">
                          <Link color="gray" size="2" asChild>
                            <button type="button">{introPage.title}</button>
                          </Link>
                        </Dialog.Trigger>
                        <Dialog.Content>
                          <Flex justify="between" gap="2" mb="1">
                            <Dialog.Title>{introPage.title}</Dialog.Title>
                            <Dialog.Close>
                              <IconButton variant="ghost">
                                <Close color="action" />
                              </IconButton>
                            </Dialog.Close>
                          </Flex>
                          <Dialog.Description>
                            <Typography variant="h3">{introPage.title}</Typography>
                            <Markdown variant="largeBody">
                              {replacePlaceholders(introPage.body, placeholders)}
                            </Markdown>
                          </Dialog.Description>
                        </Dialog.Content>
                      </Dialog.Root>
                    </li>
                  );
                })}
            </ul>
          </Flex>
        </div>
      ) : null}
    </div>
  );
}

export default function ProgressPopper({
  onClickClose,
  isOpen,
  anchorEl,
}: ProgressPopperProps): JSX.Element {
  return (
    <Popper isOpen={isOpen} onClickClose={onClickClose} anchorEl={anchorEl}>
      <ApplicationProgress onNavigate={onClickClose} />
    </Popper>
  );
}
